import type { FC, ReactNode } from 'react';
import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import {
  LocationOnOutlined as LocationIcon,
  PhoneOutlined as PhoneIcon,
  SmartphoneOutlined as SmartphoneIcon,
} from '@mui/icons-material';
import { Box, Typography, useTheme } from '@mui/material';
import clsx from 'clsx';
import {
  format as formatTime,
  intervalToDuration,
  parse as parseTime,
} from 'date-fns';

import type { Appointment, Customer } from 'src/types';
import { getOS } from 'src/utils';
import type { Theme } from 'src/theme/types';

import useStyles from './AppointmentInformation.styles';

export const AppointmentInformation: FC<{
  appointment: Appointment;
  className?: string;
  customer: Customer;
  icon?: ReactNode;
}> = ({ appointment, className, customer, icon, ...props }) => {
  const now = new Date();
  const classes = useStyles();
  const theme = useTheme<Theme>();
  const { t } = useTranslation();
  const OS = getOS();
  const startTime = parseTime(appointment.startTime, 'HH:mm:ss', now);
  const geoQuery = encodeURIComponent(
    `${customer.street} ${customer.streetNo}, ${customer.zipCode} ${customer.city}`,
  );
  const geoURL =
    OS === 'iOS' ? `https://maps.apple.com/` : `https://maps.google.com/`;
  const startTimeFormatted = formatTime(startTime, 'HH:mm');
  const endTime = parseTime(appointment.endTime, 'HH:mm:ss', now);
  const duration = intervalToDuration({
    end: endTime,
    start: startTime,
  });

  /**
   * Handle map redirect depending on OS.
   */
  const handleAddressClick = () =>
    (window.location.href = `${geoURL}?q=${geoQuery}`);

  const phoneNumbers = [
    {
      icon: <PhoneIcon />,
      key: 'tel',
      label: (n: string) => t('AppointmentInformation.tel', { telNo: n }),
      number: customer.phonePrivate,
    },
    {
      icon: <SmartphoneIcon />,
      key: 'mobile1',
      label: (n: string) => t('AppointmentInformation.mobile1', { telNo: n }),
      number: customer.phoneMobile1,
    },
  ].filter(({ number }) => number);

  return (
    <Box className={clsx(classes.root, className)} {...props}>
      <Box className={classes.iconWrapper} />

      <Box className={classes.textWrapper}>
        <Typography
          align="center"
          className={classes.textIcon}
          color="textSecondary"
          onClick={handleAddressClick}
          variant="h6"
        >
          <LocationIcon />

          {`${customer.street} ${customer.streetNo} | ${customer.zipCode} ${
            customer.city
          }${customer.quarter ? `, ${customer.quarter}` : ''}`}
        </Typography>

        <Box className={classes.phoneWrapper}>
          {phoneNumbers.map(({ icon, key, label, number }) => (
            <Typography
              key={key}
              align="center"
              className={classes.textIcon}
              color="textSecondary"
              onClick={() => (window.location.href = `tel:${number}`)}
              variant="h6"
            >
              {icon}
              {label(number!)}
            </Typography>
          ))}
        </Box>

        <Typography
          align="center"
          className={classes.durationWrapper}
          variant="h6"
        >
          <span style={{ marginRight: theme.spacing(2) }}>
            <Trans
              i18nKey={'AppointmentInformation.start'}
              values={{ startTimeFormatted }}
            />
          </span>

          <Trans
            i18nKey={
              duration.minutes
                ? 'AppointmentInformation.duration'
                : 'AppointmentInformation.durationFullHour'
            }
            values={{ ...duration }}
          />
        </Typography>
      </Box>

      <Box className={classes.iconWrapper}>{icon}</Box>
    </Box>
  );
};

export default AppointmentInformation;
